import React, { useEffect, useCallback, useMemo, useContext } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import styled from 'styled-components'
import { rem } from 'polished'

import { getUrlOfferListFilters } from 'selectors/routerSelectors'
import HeadData from 'components/Common/HeadData'
import config from 'constants/config'

import { LUXURY_ESCAPES } from 'constants/brands'

import { OFFER_TYPE_CRUISE, OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2 } from 'constants/offer'

import { BANNER_LOCATION_TOURS_AND_CRUISES } from 'constants/bannerLocations'
import { fetchBanners, fetchListBanners } from 'actions/ContentActions'

import { TOUR_V2_SEARCH_HERO_IMAGE_ID } from 'constants/tours'
import TourV2SearchControls from 'components/Search/TourV2/TourV2SearchControls'
import GlobalSearchContextProvider from 'contexts/GlobalSearch/GlobalSearchContextProvider'
import { useGlobalToursSearchContext } from 'hooks/GlobalSearch/GlobalSearchVerticals/useGlobalToursSearch'
import LayoutContainer from 'components/Common/LayoutContainer'
import GeoContext from 'contexts/geoContext'
import { OfferListEvents, OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import GlobalMobileSearchHeroBar from 'components/SearchV2/Components/Mobile/GlobalMobileSearchHeroBar'
import tourV2VerticalNameSelector from 'selectors/TourV2/tourV2VerticalNameSelector'
import { isStandaloneCruiseEnabled } from 'selectors/cruiseOfferSelectors'
import PromptCardSelector from 'components/Common/StickyPromptCard/PromptCardSelector'
import ErrorBoundary from 'components/Common/ErrorBoundary/ErrorBoundary'
import ToursPageHighIntentTours from './ToursPageHighIntentTours'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import { isManualSortingEnabledFor } from 'selectors/featuresSelectors'
import TourV2TrendingDestinationsCarousel from 'components/OfferPage/TourV2/TourV2TrendingDestinationsCarousel'
import { getTourV2TrendingDestinations } from 'components/OfferPage/TourV2/TourV2TrendingLocationsList'
import TourUSPContainer from 'components/OfferPage/TourV2/TourUSPContainer'
import * as Analytics from 'analytics/analytics'
import { clientEvent, tourCarouselHomepage, tourTrendingDestination } from 'analytics/snowplow/events'
import TourSearchHeroContainer from 'components/Search/TourV2/TourSearchHeroContainer'
import TourSearchContainerPane, {
  TourSearchContainerPaneContent,
} from 'components/Search/TourV2/TourSearchContainerPane'
import TourV2ExclusiveOfferCarousel from 'components/OfferList/OfferListTiles/TourV2ExclusiveOfferCarousel'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { getListName, productClickSnowplow } from 'analytics/enhanced-ecommerce'
import { productClickObject } from 'analytics/eventDefinitions'
import uuidV4 from 'lib/string/uuidV4Utils'
import Group from 'components/utils/Group'
import TourPageCategoriesCarousel from 'components/Tours/TourPageCategoriesCarousel'
import Places from 'constants/places'
import TourPageSearchCarousel from 'components/Tours/TourPageSearchCarousel'
import { useTourV2SearchEnabled } from 'hooks/useTourV2SearchEnabled'
import TourPageOperatorsCarousel from 'components/Tours/TourPageOperatorsCarousel'
import { EmptyArray, arrayToObject } from 'lib/array/arrayUtils'
import { TOUR_ONLY_REGIONS } from 'constants/config/region'

const OffersContainer = styled.div`
  width: 100%;
  background-color: ${props => props.theme.palette.neutral.default.eight};
  padding-bottom: ${rem(16)};
  padding-top: ${rem(32)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(48)};
  }
`

const LayoutContainerSearchBarMobile = styled(LayoutContainer)`
  position: absolute;
  bottom: 0;
  transform:translateY(50%);
`

interface MappedStateProps {
  banners: Array<App.Banner>
  listBanners: Array<App.ListBanner>
  urlFilters: App.OfferListFilters
  toursVerticalName: string,
  standaloneCruiseEnabled: boolean,
}

interface Props { }

const filterTypes: Array<App.OfferListOfferType> = [OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2]

const toursDestinations = ['Egypt', 'New York', 'Africa', 'Europe']

const exclusiveCarousel = {
  code: 'luxexclusive',
  title: <>Explore our <i>exclusive</i> tours</>,
  locations: undefined,
}

const categoryCarousels = [
  {
    code: 'foodandculinarytours',
    title: <><i>Foodie</i> favourites</>,
    locations: [],
  },
  {
    code: 'wildlifesafaris',
    title: <>Be an explorer on a <i>safari</i> adventure</>,
    locations: [Places.SouthAfrica],
  },
  {
    code: 'railjourneys',
    title: <>Scenic <i>rail</i> journeys</>,
    locations: undefined,
  },
]

const ttcOperatedCurrencies = new Set(['AUD', 'NZD', 'GBP', 'USD', 'CAD', 'EUR'])

function ToursPage(props: Props & MappedStateProps) {
  const {
    urlFilters,
    toursVerticalName,
    standaloneCruiseEnabled,
  } = props
  const isManualSortingEnabled = isManualSortingEnabledFor('tours')
  const { currentRegionCode, currentCurrency } = useContext(GeoContext)
  const isTourV2SearchEnabled = useTourV2SearchEnabled()
  const showOperatorCarousel = ttcOperatedCurrencies.has(currentCurrency)

  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchBanners(BANNER_LOCATION_TOURS_AND_CRUISES))
    dispatch(fetchListBanners(BANNER_LOCATION_TOURS_AND_CRUISES))
  }, [dispatch])

  const filters = useMemo((): App.OfferListFilters => {
    if (isManualSortingEnabled && urlFilters.locations?.length === 0) {
      const isTourOnlyRegion = TOUR_ONLY_REGIONS.includes(currentRegionCode)
      const sortDefault = isTourOnlyRegion ? 'vertical' : 'vertical-tour'
      return {
        ...urlFilters,
        offerTypes: isTourOnlyRegion ? [OFFER_TYPE_TOUR_V2] : [OFFER_TYPE_TOUR_V2, OFFER_TYPE_TOUR, OFFER_TYPE_CRUISE],
        sortBy: urlFilters.sortBy ?? sortDefault,
        limit: 250,
      }
    }
    const finalFilters = {
      ...urlFilters,
      offerTypes: filterTypes,
      frontPageVisibleOnly: true,
    }
    if (standaloneCruiseEnabled) {
      finalFilters.holidayTypes = ['!Cruises']
    }
    return finalFilters
  }, [urlFilters, standaloneCruiseEnabled, isManualSortingEnabled, currentRegionCode])

  const headTitle = config.BRAND === LUXURY_ESCAPES ?
    `Luxury ${toursVerticalName}` :
    `${toursVerticalName}`
  const description = config.BRAND === LUXURY_ESCAPES ?
    'Embark on a life changing tour or cruise. Curated by travel experts to save you time. Featuring luxury accommodation, select dining and exclusive inclusions.' :
    undefined

  const listAnalytics = useMemo(() => {
    const categoryLists = categoryCarousels.map(carousel => ({
      listId: uuidV4(),
      listName: getListName('OfferList', `tourspage - ${carousel.code}`, 'carousel'),
      key: carousel.code,
    }))

    return {
      topexclusive: { listId: uuidV4(), listName: getListName('OfferList', 'tourspage - topexclusive', 'carousel') },
      luxexclusive: { listId: uuidV4(), listName: getListName('OfferList', 'tourspage - luxexclusive', 'carousel') },
      ...arrayToObject(categoryLists, list => list.key),
    }
  }, [])

  const { globalToursSearchDispatch, globalToursSearchState } = useGlobalToursSearchContext()

  const onTourCarrouselAnalyticEvent = useCallback((action: OfferListEvents, data: any, category: string) => {
    if (action === OfferListEvents.impression) {
      Analytics.track('view-tour-carousel-homepage', tourCarouselHomepage(category))
    }

    if (action === OfferListEvents.productClick && 'offer' in data) {
      const { offer, position } = data
      const list = listAnalytics[category]
      Analytics.track(
        'client-event',
        clientEvent({
          subject: 'click-any-offer',
          action: 'clicked',
          category: 'logging',
          type: 'operational',
          optimizelyEventId: '28377200726',
          optimizelyEventKey: 'click-any-offer',
        }),
      )
      productClickSnowplow(currentCurrency, productClickObject(offer, position + 1), list)
    }
  }, [currentCurrency, listAnalytics])

  const onTourTrendingDestinationsAnalyticEvent = useCallback((action: OfferListEvents, data: any) => {
    if (action === OfferListEvents.clientAction) {
      const { subject, action } = data
      Analytics.track('click-tour-trending-destinations', tourTrendingDestination(subject, action))
    }
  }, [])

  return <AnalyticsPageContext.Provider value={AnalyticsPage.verticalPage} >
    <div>
      <HeadData
        title={`${headTitle} - ${config.title} ${currentRegionCode}`}
        description={description}
      />
      <ErrorBoundary fallback={null}>
        <PromptCardSelector tripReengagement />
      </ErrorBoundary>

      <GlobalSearchContextProvider
        state={globalToursSearchState}
        dispatch={globalToursSearchDispatch}
      >
        {isTourV2SearchEnabled && <TourSearchHeroContainer
          heroImageId={TOUR_V2_SEARCH_HERO_IMAGE_ID}
          title={<><i>Tour</i> the world with us</>}
          subtitle="Choose tours from diverse durations, locations, and types to craft your ideal adventure"
          gravity="north"
        >
          <TourSearchContainerPane>
            <TourSearchContainerPaneContent>
              <TourV2SearchControls />
            </TourSearchContainerPaneContent>
          </TourSearchContainerPane>
          <LayoutContainerSearchBarMobile>
            <GlobalMobileSearchHeroBar placeholder="Search tours in" placeholderTextList={toursDestinations} />
          </LayoutContainerSearchBarMobile>
        </TourSearchHeroContainer>}
        <>
          <OfferListEventsProvider
            onListEvent={onTourCarrouselAnalyticEvent}
            additionalData="topexclusive"
          >
            <TourV2ExclusiveOfferCarousel filters={filters} />
          </OfferListEventsProvider>
          <OffersContainer>
            <Group direction="vertical" gap={32} tabletGap={40}>
              <ErrorBoundary fallback={null}>
                <ToursPageHighIntentTours />
              </ErrorBoundary>
              <Group direction="vertical" gap={56} tabletGap={80}>
                <OfferListEventsProvider onListEvent={onTourTrendingDestinationsAnalyticEvent}>
                  <TourV2TrendingDestinationsCarousel locations={getTourV2TrendingDestinations()} />
                </OfferListEventsProvider>
                <OfferListEventsProvider
                  onListEvent={onTourCarrouselAnalyticEvent}
                  additionalData="luxexclusive"
                >
                  <TourPageSearchCarousel
                    filters={filters}
                    key={exclusiveCarousel.code}
                    title={exclusiveCarousel.title}
                    category={exclusiveCarousel.code}
                    places={exclusiveCarousel.locations}
                    sortType="vertical"
                  />
                </OfferListEventsProvider>
                <TourPageCategoriesCarousel />
                {config.BRAND === LUXURY_ESCAPES && showOperatorCarousel && <OfferListEventsProvider
                  onListEvent={onTourCarrouselAnalyticEvent}
                  additionalData="operators"
                >
                  <LayoutContainer>
                    <TourUSPContainer />
                  </LayoutContainer>
                </OfferListEventsProvider>}
                {categoryCarousels.map(carousel =>
                  <OfferListEventsProvider
                    onListEvent={onTourCarrouselAnalyticEvent}
                    key={carousel.code}
                    additionalData={carousel.code}
                  >
                    <TourPageSearchCarousel
                      title={carousel.title}
                      category={carousel.code}
                      places={carousel.locations}
                      sortType="vertical"
                    />
                  </OfferListEventsProvider>,
                )}
                <TourPageOperatorsCarousel />
              </Group>
            </Group>
          </OffersContainer>
        </>
      </GlobalSearchContextProvider>
    </div>
  </AnalyticsPageContext.Provider>
}

export default connect<MappedStateProps, void, Props, App.State>((state) => {
  return {
    listBanners: state.content.listBanners?.toursAndCruises ?? EmptyArray,
    banners: state.content.banners.toursAndCruises,
    urlFilters: getUrlOfferListFilters(state),
    toursVerticalName: tourV2VerticalNameSelector(state),
    standaloneCruiseEnabled: isStandaloneCruiseEnabled(state),
  }
})(ToursPage)
